import React, { useState, useEffect, useRef } from 'react'
import { Form, Row, Col, Button } from 'react-bootstrap'
import { SECONDS_PER_DAY } from './timeInQueueUnit'

// Number filter for a column that stores seconds but is always displayed
// as days. Values typed into Min / Max / Equals are interpreted in days,
// then converted back to seconds before being written into `params` for
// the API.
function TimeInQueueFilterPopover({
  column,
  params,
  setParams,
  onClose,
  onSetHandleApply,
  canEdit = true,
  hideApplyButton = false
}) {
  const prefix = column.section_abbreviation?.toLowerCase() || ''
  const suffix = column.accessor_key.slice(prefix.length)
  const minAccessorKey = `${prefix}Min${suffix}`
  const maxAccessorKey = `${prefix}Max${suffix}`
  const operatorKey = `${column.accessor_key}Operator`
  const operatorValueKey = `${column.accessor_key}OperatorValue`

  // Seconds <-> days conversions. We round-trip to strings so the user
  // sees what they typed and so the input doesn't reformat under them.
  const fromSeconds = (seconds) => {
    if (seconds === '' || seconds === null || seconds === undefined) return ''
    const num = Number(seconds)
    if (Number.isNaN(num)) return ''
    const days = num / SECONDS_PER_DAY
    return Number.isInteger(days) ? String(days) : String(Number(days.toFixed(2)))
  }
  const toSeconds = (display) => {
    if (display === '' || display === null || display === undefined) return ''
    const num = Number(display)
    if (Number.isNaN(num)) return ''
    return Math.round(num * SECONDS_PER_DAY)
  }

  const [operator, setOperator] = useState(params[operatorKey] || 'range')
  const [operatorDisplay, setOperatorDisplay] = useState(fromSeconds(params[operatorValueKey]))

  const [minSeconds, setMinSeconds] = useState(params[minAccessorKey] || '')
  const [maxSeconds, setMaxSeconds] = useState(params[maxAccessorKey] || '')
  const [minDisplay, setMinDisplay] = useState(fromSeconds(params[minAccessorKey]))
  const [maxDisplay, setMaxDisplay] = useState(fromSeconds(params[maxAccessorKey]))

  const initialMinSeconds = useRef(params[minAccessorKey] || '')
  const initialMaxSeconds = useRef(params[maxAccessorKey] || '')
  const initialOperator = useRef(params[operatorKey] || 'range')
  const initialOperatorValue = useRef(params[operatorValueKey] || '')

  const showRangeInputs = operator === 'range'
  const showSingleInput = operator !== 'range'

  const handleOperatorChange = (e) => {
    if (!canEdit) return
    const next = e.target.value
    setOperator(next)
    if (next === 'range') {
      setOperatorDisplay('')
    } else {
      setMinSeconds('')
      setMaxSeconds('')
      setMinDisplay('')
      setMaxDisplay('')
    }
  }

  const handleMinChange = (e) => {
    if (!canEdit) return
    const display = e.target.value
    setMinDisplay(display)
    setMinSeconds(toSeconds(display))
  }

  const handleMaxChange = (e) => {
    if (!canEdit) return
    const display = e.target.value
    setMaxDisplay(display)
    setMaxSeconds(toSeconds(display))
  }

  const handleOperatorValueChange = (e) => {
    if (!canEdit) return
    setOperatorDisplay(e.target.value)
  }

  const buildParams = (currentParams) => {
    const newParams = { ...currentParams }

    if (operator === 'range') {
      newParams[minAccessorKey] = minSeconds
      newParams[maxAccessorKey] = maxSeconds
      delete newParams[operatorKey]
      delete newParams[operatorValueKey]
    } else {
      // Greater Than and Less Than both take a single value in days,
      // converted to seconds for the API.
      newParams[minAccessorKey] = ''
      newParams[maxAccessorKey] = ''
      newParams[operatorKey] = operator
      newParams[operatorValueKey] = toSeconds(operatorDisplay)
    }
    return newParams
  }

  const handleApply = () => {
    const hasChanges =
      minSeconds !== initialMinSeconds.current ||
      maxSeconds !== initialMaxSeconds.current ||
      operator !== initialOperator.current ||
      toSeconds(operatorDisplay) !== initialOperatorValue.current
    if (hasChanges) {
      setParams((p) => buildParams(p))
    }
    onClose()
  }

  useEffect(() => {
    if (onSetHandleApply) onSetHandleApply(handleApply)
  }, [minSeconds, maxSeconds, operator, operatorDisplay]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (hideApplyButton) {
      setParams((p) => buildParams(p))
    }
  }, [minSeconds, maxSeconds, operator, operatorDisplay, hideApplyButton]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleClear = () => {
    setMinSeconds('')
    setMaxSeconds('')
    setMinDisplay('')
    setMaxDisplay('')
    setOperator('range')
    setOperatorDisplay('')
    setParams((p) => {
      const next = {
        ...p,
        [minAccessorKey]: '',
        [maxAccessorKey]: ''
      }
      delete next[operatorKey]
      delete next[operatorValueKey]
      return next
    })
  }

  return (
    <div>
      <div className="mb-3">
        <strong>{column.name}</strong>
      </div>

      <div className="mb-3">
        <Form.Select value={operator} onChange={handleOperatorChange} size="sm" disabled={!canEdit}>
          <option value="range">Range</option>
          <option value="greater_than">Greater Than</option>
          <option value="less_than">Less Than</option>
        </Form.Select>
      </div>

      {showRangeInputs && (
        <Row className="g-3 mb-3">
          <Col>
            <Form.Label className="small">Minimum (days)</Form.Label>
            <Form.Control
              type="number"
              placeholder="Min days"
              value={minDisplay}
              onChange={handleMinChange}
              step={'any'}
              size="sm"
              disabled={!canEdit}
            />
          </Col>
          <Col>
            <Form.Label className="small">Maximum (days)</Form.Label>
            <Form.Control
              type="number"
              placeholder="Max days"
              value={maxDisplay}
              onChange={handleMaxChange}
              step={'any'}
              size="sm"
              disabled={!canEdit}
            />
          </Col>
        </Row>
      )}

      {showSingleInput && (
        <div className="mb-3">
          <Form.Label className="small">Value (days)</Form.Label>
          <Form.Control
            type="number"
            placeholder="Value (days)"
            value={operatorDisplay}
            onChange={handleOperatorValueChange}
            step={'any'}
            size="sm"
            disabled={!canEdit}
          />
        </div>
      )}

      {!hideApplyButton && (
        <div className="d-flex justify-content-between gap-2">
          {canEdit && (
            <Button variant="light" size="sm" onClick={handleClear} className="text-primary">
              Clear
            </Button>
          )}
          <Button variant="primary" size="sm" onClick={canEdit ? handleApply : onClose}>
            {canEdit ? 'Apply' : 'Close'}
          </Button>
        </div>
      )}
    </div>
  )
}

export default TimeInQueueFilterPopover
